
import { useState, useEffect } from 'react';

export default function AgricultureCalculator() {
  const [acres, setAcres] = useState(100);
  const [serviceType, setServiceType] = useState<'spraying' | 'mapping' | 'deerSurvey' | 'fullProgram'>('spraying');
  const [sprayingType, setSprayingType] = useState<'herbicide' | 'fungicide' | 'coverCrop'>('herbicide');
  const [terrainType, setTerrainType] = useState<'flat' | 'rolling' | 'complex'>('flat');
  const [priorityTurnaround, setPriorityTurnaround] = useState(false);
  const [outsideMobilization, setOutsideMobilization] = useState(false);

  const [perAcreRateLow, setPerAcreRateLow] = useState(0);
  const [perAcreRateHigh, setPerAcreRateHigh] = useState(0);
  const [totalLow, setTotalLow] = useState(0);
  const [totalHigh, setTotalHigh] = useState(0);

  useEffect(() => {
    calculatePricing();
  }, [acres, serviceType, sprayingType, terrainType, priorityTurnaround]);

  const calculatePricing = () => {
    let baseLow = 0;
    let baseHigh = 0;

    // Base rates per acre
    switch (serviceType) {
      case 'spraying':
        switch (sprayingType) {
          case 'herbicide':
            baseLow = 8;
            baseHigh = 15;
            break;
          case 'fungicide':
            baseLow = 10;
            baseHigh = 18;
            break;
          case 'coverCrop':
            baseLow = 12;
            baseHigh = 22;
            break;
        }
        break;
      case 'mapping':
        baseLow = 3;
        baseHigh = 8;
        break;
      case 'deerSurvey':
        baseLow = 4;
        baseHigh = 10;
        break;
      case 'fullProgram':
        baseLow = 18;
        baseHigh = 35;
        break;
    }

    // Terrain multiplier
    let terrainMultiplier = 1.0;
    switch (terrainType) {
      case 'flat':
        terrainMultiplier = 1.0;
        break;
      case 'rolling':
        terrainMultiplier = 1.15;
        break;
      case 'complex':
        terrainMultiplier = 1.35;
        break;
    }

    // Small-site premium (scales down to 200 acres for ag)
    const smallSiteMultiplier = acres >= 200 
      ? 1.0 
      : 1.4 - ((Math.min(acres, 200) - 1) * (0.4 / 199));

    // Combined multiplier
    const combinedMultiplier = terrainMultiplier * smallSiteMultiplier;

    // Adjusted rates
    const adjustedLow = baseLow * combinedMultiplier;
    const adjustedHigh = baseHigh * combinedMultiplier;

    setPerAcreRateLow(adjustedLow);
    setPerAcreRateHigh(adjustedHigh);

    // Total estimate
    let low = acres * adjustedLow;
    let high = acres * adjustedHigh;

    // Apply priority turnaround
    if (priorityTurnaround) {
      low *= 1.15;
      high *= 1.15;
    }

    // Minimum charge
    low = Math.max(low, 350);
    high = Math.max(high, 500);

    setTotalLow(Math.round(low));
    setTotalHigh(Math.round(high));
  };

  const handleAcresChange = (value: number) => {
    const clampedValue = Math.max(10, Math.min(2000, value));
    setAcres(clampedValue);
  };

  const getServiceDescription = () => {
    switch (serviceType) {
      case 'spraying':
        return 'Precision drone application for herbicides, fungicides, or cover crop seeding.';
      case 'mapping':
        return 'High-resolution aerial mapping for crop health, drainage analysis, and field planning.';
      case 'deerSurvey':
        return 'Thermal drone surveys to assess deer population and movement patterns.';
      case 'fullProgram':
        return 'Seasonal program combining spraying, mapping, and monitoring throughout the growing season.';
    }
  };

  return (
    <div id="agriculture-calculator" className="bg-white rounded-lg shadow-sm border border-stone-200 p-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-stone-900 mb-3">Agriculture Drone Services Estimator</h2>
        <p className="text-stone-600">
          Select your service type and field size for an instant estimate. Pricing adjusts for terrain complexity and field size.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Calculator Inputs */}
        <div className="space-y-6">
          {/* Service Type */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Service Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="agService"
                  value="spraying"
                  checked={serviceType === 'spraying'}
                  onChange={() => setServiceType('spraying')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <span className="text-sm font-medium text-stone-900">Drone Spraying</span>
                  <p className="text-xs text-stone-500">Herbicide, fungicide, or cover crop application</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="agService"
                  value="mapping"
                  checked={serviceType === 'mapping'}
                  onChange={() => setServiceType('mapping')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <span className="text-sm font-medium text-stone-900">Field Mapping &amp; Analysis</span>
                  <p className="text-xs text-stone-500">Crop health, drainage, elevation maps</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="agService"
                  value="deerSurvey"
                  checked={serviceType === 'deerSurvey'}
                  onChange={() => setServiceType('deerSurvey')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <span className="text-sm font-medium text-stone-900">Deer Survey</span>
                  <p className="text-xs text-stone-500">Thermal population assessment</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="agService"
                  value="fullProgram"
                  checked={serviceType === 'fullProgram'}
                  onChange={() => setServiceType('fullProgram')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <div>
                  <span className="text-sm font-medium text-stone-900">Seasonal Program</span>
                  <p className="text-xs text-stone-500">Full-season spraying + mapping package</p>
                </div>
              </label>
            </div>
          </div>

          {/* Spraying Type (conditional) */}
          {serviceType === 'spraying' && (
            <div>
              <label className="block text-sm font-semibold text-stone-900 mb-3">
                Application Type
              </label>
              <select
                value={sprayingType}
                onChange={(e) => setSprayingType(e.target.value as 'herbicide' | 'fungicide' | 'coverCrop')}
                className="w-full px-4 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="herbicide">Herbicide Application</option>
                <option value="fungicide">Fungicide Application</option>
                <option value="coverCrop">Cover Crop Seeding</option>
              </select>
            </div>
          )}

          {/* Field Size */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Field Size (Acres)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="10"
                max="2000"
                step="10"
                value={acres}
                onChange={(e) => handleAcresChange(Number(e.target.value))}
                className="flex-1 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
              <input
                type="number"
                min="10"
                max="2000"
                value={acres}
                onChange={(e) => handleAcresChange(Number(e.target.value))}
                className="w-24 px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <p className="text-xs text-stone-500 mt-2">Minimum 10 acres</p>
          </div>

          {/* Terrain Type */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Terrain Complexity
            </label>
            <select
              value={terrainType}
              onChange={(e) => setTerrainType(e.target.value as 'flat' | 'rolling' | 'complex')}
              className="w-full px-4 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="flat">Flat / Open Fields</option>
              <option value="rolling">Rolling Hills / Some Obstacles</option>
              <option value="complex">Complex / Wooded Edges / Irregular Shape</option>
            </select>
          </div>

          {/* Add-ons */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Add-ons
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="checkbox"
                  checked={priorityTurnaround}
                  onChange={(e) => setPriorityTurnaround(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-stone-900">Priority scheduling: +15%</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="checkbox"
                  checked={outsideMobilization}
                  onChange={(e) => setOutsideMobilization(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-stone-900">Outside 60 miles mobilization</span>
                  <p className="text-xs text-stone-500 mt-1">Travel billed separately</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Results Panel */}
        <div className="bg-stone-50 rounded-lg p-6 border border-stone-200 h-fit">
          <h3 className="text-xl font-bold text-stone-900 mb-4">Estimated Pricing</h3>
          
          <div className="bg-emerald-50 rounded-lg p-3 mb-6 border border-emerald-200">
            <p className="text-sm text-emerald-800">{getServiceDescription()}</p>
          </div>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center pb-3 border-b border-stone-300">
              <span className="text-sm font-medium text-stone-600">Field Size</span>
              <span className="text-lg font-bold text-stone-900">{acres.toLocaleString()} acres</span>
            </div>
            
            <div className="flex justify-between items-center pb-3 border-b border-stone-300">
              <span className="text-sm font-medium text-stone-600">Per-Acre Rate</span>
              <span className="text-lg font-bold text-stone-900">
                ${perAcreRateLow.toFixed(2)} to ${perAcreRateHigh.toFixed(2)}
              </span>
            </div>
            
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <div className="text-sm font-medium text-emerald-800 mb-2">Estimated Price Range</div>
              <div className="text-3xl font-bold text-emerald-900">
                ${totalLow.toLocaleString()} to ${totalHigh.toLocaleString()}
              </div>
            </div>
          </div>
          
          <p className="text-xs text-stone-600 mb-6">
            Final pricing depends on crop type, chemical requirements, and field access. Minimum charge applies to small fields.
          </p>
          
          <div className="space-y-3">
            <a
              href="/instant-quote"
              className="block w-full bg-emerald-700 text-white text-center px-6 py-3 rounded-md font-semibold hover:bg-emerald-800 transition-colors whitespace-nowrap"
            >
              Get a Firm Quote
            </a>
            <a
              href="tel:+1234567890"
              className="block w-full bg-white text-stone-900 text-center px-6 py-3 rounded-md font-semibold border border-stone-300 hover:bg-stone-50 transition-colors whitespace-nowrap"
            >
              Call for Details
            </a>
          </div>
        </div>
      </div>

      {/* What You Get Panel */}
      <div className="mt-10 pt-8 border-t border-stone-200">
        <h3 className="text-xl font-bold text-stone-900 mb-6">What You Get</h3>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-stone-50 rounded-lg p-5 border border-stone-200">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-spray-line text-xl text-emerald-700"></i>
            </div>
            <h4 className="font-semibold text-stone-900 mb-2">Drone Spraying</h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• Precision application</li>
              <li>• GPS flight logs</li>
              <li>• Coverage map</li>
              <li>• Application report</li>
            </ul>
          </div>
          <div className="bg-stone-50 rounded-lg p-5 border border-stone-200">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-map-2-line text-xl text-emerald-700"></i>
            </div>
            <h4 className="font-semibold text-stone-900 mb-2">Field Mapping</h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• High-res orthomosaic</li>
              <li>• NDVI crop health map</li>
              <li>• Elevation model</li>
              <li>• Problem area markers</li>
            </ul>
          </div>
          <div className="bg-stone-50 rounded-lg p-5 border border-stone-200">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-focus-3-line text-xl text-emerald-700"></i>
            </div>
            <h4 className="font-semibold text-stone-900 mb-2">Deer Survey</h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• Thermal flight coverage</li>
              <li>• Population count</li>
              <li>• Movement patterns</li>
              <li>• Detailed report</li>
            </ul>
          </div>
          <div className="bg-stone-50 rounded-lg p-5 border border-stone-200">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <i className="ri-calendar-check-line text-xl text-emerald-700"></i>
            </div>
            <h4 className="font-semibold text-stone-900 mb-2">Seasonal Program</h4>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• Multiple spray passes</li>
              <li>• Seasonal mapping</li>
              <li>• Priority scheduling</li>
              <li>• Season summary report</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
